import { gql, useQuery } from '@apollo/client';
import { PhotoWhereInput } from '__generated__/queries-photo';
import { useMemo } from 'react';
import { Metadata } from 'utils/getMetadata';

interface UnderSubjectMetadata {
  id: number;
  value: string;
}

interface GetUnderSubjectsQuery {
  searchPhotos: {
    id: string;
    Metadata_Undersubject: { id: number; value?: string | null }[];
  }[];
}

interface GetUnderSubjectsQueryVariables {
  where: PhotoWhereInput;
  first: number;
}

export const GET_UNDER_SUBJECTS = gql`
  query GetUnderSubjects($where: PhotoWhereInput!, $first: Int!) {
    searchPhotos(where: $where, first: $first) {
      id
      Metadata_Undersubject(first: 1) {
        id
        value
      }
    }
  }
`;

const getSubjectPhotosWhere = (
  subjectId: string,
  archiveId?: number,
): PhotoWhereInput => ({
  isUploaded: true,
  AND: [
    {
      photoMetadatas_some: {
        value: subjectId,
        metadata: { name: Metadata.ID_subject },
      },
    },
  ],
  ...(archiveId ? { archive: { archiveId } } : {}),
});

const extractUnderSubjects = (
  photos: GetUnderSubjectsQuery['searchPhotos'] | undefined,
) => {
  const values = new Map<string, UnderSubjectMetadata>();
  let hasPhotosWithoutUndersubject = false;

  photos?.forEach((photo) => {
    const [undersubject] = photo.Metadata_Undersubject;

    if (undersubject?.value) {
      if (!values.has(undersubject.value)) {
        values.set(undersubject.value, {
          id: undersubject.id,
          value: undersubject.value,
        });
      }
      return;
    }

    hasPhotosWithoutUndersubject = true;
  });

  return {
    // eslint-disable-next-line fp/no-mutating-methods
    metadatas: [...values.values()].sort((a, b) =>
      a.value.localeCompare(b.value),
    ),
    hasPhotosWithoutUndersubject,
  };
};

export const useGetUnderSubjects = (
  subjectId: string,
  archiveId?: number,
  skip?: boolean,
) => {
  const where = useMemo(() => getSubjectPhotosWhere(subjectId, archiveId), [
    subjectId,
    archiveId,
  ]);

  const { data, ...rest } = useQuery<
    GetUnderSubjectsQuery,
    GetUnderSubjectsQueryVariables
  >(GET_UNDER_SUBJECTS, {
    variables: {
      first: 100000,
      where,
    },
    notifyOnNetworkStatusChange: true,
    skip: skip || !subjectId,
  });

  const { metadatas, hasPhotosWithoutUndersubject } = useMemo(
    () => extractUnderSubjects(data?.searchPhotos),
    [data?.searchPhotos],
  );

  return { metadatas, hasPhotosWithoutUndersubject, ...rest };
};
